import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { LocationSubset } from '@era/stores';
import { Subscription } from 'rxjs';
import { SyncTypeFilter } from './devices-sync-filter';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'era-devices-sync-modal',
  templateUrl: './devices-sync-modal.component.html',
  styleUrls: ['./devices-sync-modal.component.scss'],
  imports: [ReactiveFormsModule, NgbTooltip, FaIconComponent],
})
export class DevicesSyncModalComponent implements OnInit {
  @Input() public locationSubset: LocationSubset;
  @Input() public userIsAdmin: boolean;
  @Output() public readonly closeModal = new EventEmitter<void>();
  @Output() public readonly submitFilters = new EventEmitter<{ filter: SyncTypeFilter; force: boolean }>();

  public readonly syncDevicesForm: UntypedFormGroup;
  public readonly LocationSubset = LocationSubset;
  public selectedAllSettings = false;

  private subscriptions: Subscription[] = [];

  constructor(private readonly formBuilder: UntypedFormBuilder) {
    const controlsConfig: SyncTypeFilter & { ForceSync: boolean } = {
      Applications: false,
      NotificationProfiles: false,
      WifiProfiles: false,
      EraApplicationConfiguration: false,
      AppLockProfile: false,
      RestrictionsProfiles: false,
      DeviceName: false,
      AirplaySecurityProfile: false,
      ConfigurationProfiles: false,
      ForceSync: false,
    };

    this.syncDevicesForm = this.formBuilder.group(controlsConfig);
    this.syncDevicesForm.setValidators(this.validateCheckboxes());
  }

  ngOnInit(): void {
    // Disable WifiProfiles checkbox for non-admin users
    if (!this.userIsAdmin) {
      this.syncDevicesForm.get('WifiProfiles')?.disable();
    }
  }

  validateCheckboxes(): (x: UntypedFormGroup) => { requireOneDeviceModelChecked: boolean } {
    return (formGroup: UntypedFormGroup) => {
      let checked = 0;

      Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.controls[key];
        if (control.value && key !== 'ForceSync') {
          checked += 1;
        }
      });

      if (checked === 0) {
        return { requireOneDeviceModelChecked: true };
      }

      return null;
    };
  }

  toggleSelectAll() {
    this.selectedAllSettings = !this.selectedAllSettings;
    Object.keys(this.syncDevicesForm.controls).forEach((controlName) => {
      if (controlName !== 'ForceSync') {
        this.syncDevicesForm.controls[controlName].setValue(this.selectedAllSettings);
      }
    });
  }

  closingModal() {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
    this.closeModal.emit();
  }

  onSubmit() {
    const forceSync = this.syncDevicesForm.get('ForceSync').value;

    const syncTypeFilter: SyncTypeFilter = {
      Applications: this.syncDevicesForm.get('Applications').value,
      NotificationProfiles: this.syncDevicesForm.get('NotificationProfiles').value,
      WifiProfiles: this.syncDevicesForm.get('WifiProfiles').value,
      EraApplicationConfiguration: this.syncDevicesForm.get('EraApplicationConfiguration').value,
      AppLockProfile: this.syncDevicesForm.get('AppLockProfile').value,
      RestrictionsProfiles: this.syncDevicesForm.get('RestrictionsProfiles').value,
      DeviceName: this.syncDevicesForm.get('DeviceName').value,
      AirplaySecurityProfile: this.syncDevicesForm.get('AirplaySecurityProfile').value,
      ConfigurationProfiles: this.syncDevicesForm.get('ConfigurationProfiles').value,
    };

    this.submitFilters.emit({ filter: syncTypeFilter, force: forceSync });
    this.syncDevicesForm.reset();

    this.closingModal();
  }
}
